import { useParams } from "react-router-dom";
import { Phone, CheckCircle2, ChevronDown } from "lucide-react";
import { useState } from "react";
import { getService, SERVICES, SITE } from "@/config/site";
import { Seo } from "@/components/Seo";
import { Breadcrumbs, breadcrumbSchema } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { CallButton, InterventionButton, FinalCTA } from "@/components/blocks";
import NotFound from "@/pages/NotFound";

export default function ServicePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.serviceSlug;
  const service = getService(slug);
  const [openFaq, setOpenFaq] = useState(0);

  if (!service) return <NotFound />;

  const crumbs = [{ label: service.nav }];
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: service.h1,
    provider: { "@type": "PestControlService", name: SITE.brand, telephone: "+33635922149" },
    areaServed: "Manche, France",
    description: service.intro,
  };
  const faqSchema = service.faq?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: service.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  return (
    <>
      <Seo
        title={service.metaTitle}
        description={service.metaDescription}
        path={`/${service.slug}`}
        jsonLd={[serviceSchema, breadcrumbSchema(crumbs), ...(faqSchema ? [faqSchema] : [])]}
      />

      {/* HERO */}
      <section className="relative bg-brand-black text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={service.image} alt={service.alt} className="w-full h-full object-cover" width="1600" height="900" />
          <div className="absolute inset-0 bg-brand-black/75" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="mb-6 [&_*]:text-white/80"><Breadcrumbs items={crumbs} /></div>
          <Reveal className="max-w-3xl">
            <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-[1.08] tracking-tight">{service.h1}</h1>
            <p className="mt-5 text-lg text-white/80 leading-relaxed">{service.intro}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a href={SITE.phoneHref} data-testid="service-call-btn" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-brand-gold text-brand-black font-bold hover:-translate-y-0.5 transition-transform">
                <Phone className="w-5 h-5" /> Appeler le {SITE.phoneDisplay}
              </a>
              <InterventionButton dark testid="service-intervention-btn" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* POINTS */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-2 gap-12">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-gold">Notre approche</span>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-brand-black mt-3 mb-6">Comment nous intervenons</h2>
          <ul className="space-y-4">
            {service.points.map((p) => (
              <li key={p} className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-brand-gold shrink-0 mt-0.5" />
                <span className="text-brand-text leading-relaxed">{p}</span>
              </li>
            ))}
          </ul>
          <p className="mt-6 text-sm text-brand-text italic">
            Nous n'annonçons pas de résultat garanti, de délai précis ni de tarif fixe sans information confirmée. Nous
            vous indiquons la solution la plus adaptée à la situation observée.
          </p>
        </div>

        {/* FAQ */}
        {service.faq?.length > 0 && (
          <div>
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-brand-gold">Questions fréquentes</span>
            <h2 className="font-display text-3xl md:text-4xl font-extrabold text-brand-black mt-3 mb-6">Vos questions</h2>
            <div className="space-y-3">
              {service.faq.map((f, i) => (
                <div key={i} className="border border-gray-200 rounded-2xl overflow-hidden">
                  <button
                    data-testid={`faq-toggle-${i}`}
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between gap-3 p-5 text-left"
                    aria-expanded={openFaq === i}
                  >
                    <span className="font-semibold text-brand-black">{f.q}</span>
                    <ChevronDown className={`w-5 h-5 text-brand-gold shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && <p className="px-5 pb-5 text-sm text-brand-text leading-relaxed">{f.a}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Autres services */}
      <section className="bg-brand-gray py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-black mb-6">Autres interventions</h2>
          <div className="flex flex-wrap gap-3">
            {SERVICES.filter((s) => s.slug !== service.slug).map((s) => (
              <a key={s.slug} href={`/${s.slug}`} className="inline-flex items-center gap-2 bg-white border border-gray-200 px-5 py-3 rounded-full text-sm font-semibold text-brand-black hover:border-brand-gold transition-colors">
                {s.nav}
              </a>
            ))}
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
